const { resolveDecisionLetterGreeting, splitDecisionLetterGreeting } = require('./decisionLetterGreeting');

const formatDate = (date) => {
  if (!date) return '';
  if (typeof date === 'string' && date.length >= 10) return date.slice(0, 10);
  const d = new Date(date);
  if (isNaN(d)) return '';
  return d.toISOString().slice(0, 10);
};

const buildEmptyDecisionLetterDraft = () => ({
  decision_date: '',
  letter_title: '',
  decision_greeting: '',
  decision_intro: '',
  decision_label: '',
  decision_reason: '',
  next_step_1: '',
  next_step_2: '',
  coordinator_name: '',
  organization_name: ''
});
const normalizeDecisionDateValue = (value, fallback = '') => {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  if (!trimmed) return fallback || '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  const formatted = formatDate(trimmed);
  return formatted || trimmed;
};
const LETTER_MARKERS = {
  date: /^date\s*:/i,
  decision: /^decision\s*:/i,
  reason: /^reason\s*:/i,
  nextSteps: /^next steps?\s*:/i,
  sincerely: /^sincerely\b/i
};
const buildLetterBodyFromDraft = (
  draft = {},
  { includeNextSteps = true, includeDecisionLabel = true, includeReasonLabel = true, recipientName = '' } = {}
) => {
  const safe = value => (typeof value === 'string' ? value.trim() : '');
  const title = safe(draft.letter_title);
  const date = safe(draft.decision_date);
  const { greeting, intro } = resolveDecisionLetterGreeting(draft, recipientName);
  const decisionLabel = safe(draft.decision_label);
  const reason = typeof draft.decision_reason === 'string' ? draft.decision_reason.trim() : '';
  const nextStep1 = typeof draft.next_step_1 === 'string' ? draft.next_step_1.trim() : '';
  const nextStep2 = typeof draft.next_step_2 === 'string' ? draft.next_step_2.trim() : '';
  const coordinator = safe(draft.coordinator_name);
  const organization = safe(draft.organization_name);
  const lines = [];
  if (title) lines.push(title);
  if (date) lines.push(`Date: ${date}`);
  if (lines.length) lines.push('');
  if (greeting) lines.push(greeting, '');
  if (intro) {
    lines.push(intro);
    lines.push('');
  }
  if (includeDecisionLabel && decisionLabel) {
    lines.push(`Decision: ${decisionLabel}`);
  }
  if (reason) {
    if (includeReasonLabel) {
      if (reason.includes('\n')) {
        lines.push('Reason:');
        lines.push(reason);
      } else {
        lines.push(`Reason: ${reason}`);
      }
    } else {
      if (includeDecisionLabel && decisionLabel && lines[lines.length - 1] !== '') {
        lines.push('');
      }
      lines.push(reason);
    }
  } else if (includeReasonLabel) {
    lines.push('Reason:');
  }
  if (includeNextSteps) {
    lines.push('');
    lines.push('Next steps:');
    lines.push(nextStep1 ? `- ${nextStep1}` : '- ');
    lines.push(nextStep2 ? `- ${nextStep2}` : '- ');
  }
  lines.push('');
  lines.push('Sincerely,');
  if (coordinator) lines.push(coordinator);
  if (organization) lines.push(organization);
  return lines.join('\n').replace(/\n{3,}/g, '\n\n').trim();
};
const parseLetterBodyToDraft = (value, fallback = buildEmptyDecisionLetterDraft()) => {
  const base = Object.assign({}, fallback);
  const normalized = typeof value === 'string' ? value.replace(/\r\n/g, '\n') : '';
  const lines = normalized.split('\n');
  const trimmedLines = lines.map(line => line.trim());
  const dateIndex = trimmedLines.findIndex(line => LETTER_MARKERS.date.test(line));
  const decisionIndex = trimmedLines.findIndex(line => LETTER_MARKERS.decision.test(line));
  const reasonIndex = trimmedLines.findIndex(line => LETTER_MARKERS.reason.test(line));
  const nextStepsIndex = trimmedLines.findIndex(line => LETTER_MARKERS.nextSteps.test(line));
  const sincerelyIndex = trimmedLines.findIndex(line => LETTER_MARKERS.sincerely.test(line));
  const hasMarkers = [dateIndex, decisionIndex, reasonIndex, nextStepsIndex, sincerelyIndex].some(index => index >= 0);
  if (!hasMarkers) {
    const opening = splitDecisionLetterGreeting(normalized);
    base.decision_greeting = opening.greeting;
    base.decision_intro = opening.intro;
    return base;
  }
  let titleIndex = -1;
  if (dateIndex > 0) {
    for (let i = dateIndex - 1; i >= 0; i -= 1) {
      if (trimmedLines[i]) {
        titleIndex = i;
        break;
      }
    }
  } else if (dateIndex === -1) {
    titleIndex = trimmedLines.findIndex(line => line.length > 0);
    if (titleIndex >= 0 && splitDecisionLetterGreeting(trimmedLines[titleIndex]).greeting) {
      titleIndex = -1;
      base.letter_title = '';
    }
  }
  if (titleIndex >= 0) {
    base.letter_title = lines[titleIndex].trim();
  } else if (dateIndex === 0) {
    base.letter_title = '';
  }
  if (dateIndex >= 0) {
    const dateValue = lines[dateIndex].replace(LETTER_MARKERS.date, '').trim();
    base.decision_date = normalizeDecisionDateValue(dateValue, base.decision_date);
  }
  const introStartBase = dateIndex >= 0 ? dateIndex + 1 : (titleIndex >= 0 ? titleIndex + 1 : 0);
  const endCandidates = [decisionIndex, reasonIndex, nextStepsIndex, sincerelyIndex].filter(index => index >= 0);
  const introEnd = endCandidates.length ? Math.min(...endCandidates) : lines.length;
  let introStart = introStartBase;
  while (introStart < introEnd && !trimmedLines[introStart]) {
    introStart += 1;
  }
  const opening = splitDecisionLetterGreeting(lines.slice(introStart, introEnd).join('\n'));
  base.decision_greeting = opening.greeting;
  const hasDecisionMarker = decisionIndex >= 0;
  const hasReasonMarker = reasonIndex >= 0;
  if (!hasDecisionMarker && !hasReasonMarker) {
    const bodyLines = opening.intro.split('\n');
    const paragraphs = [];
    let current = [];
    bodyLines.forEach(line => {
      if (!line.trim()) {
        if (current.length) {
          paragraphs.push(current.join('\n').trim());
          current = [];
        }
        return;
      }
      current.push(line);
    });
    if (current.length) {
      paragraphs.push(current.join('\n').trim());
    }
    base.decision_intro = paragraphs[0] || '';
    base.decision_reason = paragraphs.slice(1).join('\n\n').trim();
  } else {
    base.decision_intro = opening.intro;
  }
  if (decisionIndex >= 0) {
    base.decision_label = lines[decisionIndex].replace(LETTER_MARKERS.decision, '').trim();
  }
  if (reasonIndex >= 0) {
    const reasonEndCandidates = [nextStepsIndex, sincerelyIndex].filter(index => index > reasonIndex);
    const reasonEnd = reasonEndCandidates.length ? Math.min(...reasonEndCandidates) : lines.length;
    const reasonLines = lines.slice(reasonIndex, reasonEnd);
    const firstLine = reasonLines[0].replace(LETTER_MARKERS.reason, '').trim();
    const rest = reasonLines.slice(1).join('\n').trim();
    base.decision_reason = [firstLine, rest].filter(Boolean).join('\n').trim();
  }
  if (nextStepsIndex >= 0) {
    const stepsEnd = sincerelyIndex > nextStepsIndex ? sincerelyIndex : lines.length;
    const stepLines = lines.slice(nextStepsIndex + 1, stepsEnd);
    const steps = [];
    stepLines.forEach(line => {
      const trimmed = line.trim();
      if (!trimmed) return;
      if (trimmed === '-' || trimmed === '*' || /^\d+[.)]$/.test(trimmed)) return;
      const bulletMatch = trimmed.match(/^[-*]\s+(.*)$/) || trimmed.match(/^\d+[.)]\s+(.*)$/);
      if (bulletMatch) {
        const content = bulletMatch[1].trim();
        if (content) steps.push(content);
        return;
      }
      if (!steps.length) {
        steps.push(trimmed);
        return;
      }
      steps[steps.length - 1] = `${steps[steps.length - 1]} ${trimmed}`.trim();
    });
    base.next_step_1 = steps[0] || '';
    base.next_step_2 = steps[1] || '';
  }
  if (sincerelyIndex >= 0) {
    const closingLines = lines.slice(sincerelyIndex + 1).map(line => line.trim()).filter(Boolean);
    base.coordinator_name = closingLines[0] || '';
    base.organization_name = closingLines[1] || '';
  }
  return base;
};

module.exports = { buildEmptyDecisionLetterDraft, buildLetterBodyFromDraft, parseLetterBodyToDraft };
